// One definition of "how big is a patron photo", shared by every onboarding
// upload path so the kiosk camera and the QR/phone upload cannot drift apart.
//
// Taken from the largest box that ever renders one of these photos: the
// capstone Act 4 portrait circles, the biggest of which is 930px on the fixed
// 11520px stage (`act4/act4DisplayConfig.js` portraitPlacements). Act 1's
// mosaic circles are 852px (7.4vw of the stage), Act 2 tops out at 600px and
// Act 5's tiles are 72px. `patronPhoto.test.js` derives that figure from those
// act configs rather than restating it, so a capstone layout that outgrows the
// bound fails CI instead of going stale here.
//
// This is a payload-size bound, not a sharpness guarantee. It caps the
// *longest* side, while every consumer renders into a square box with
// `object-fit: cover`, which scales by box / shortest side — so a non-square
// photo is still upscaled somewhat (a 3:4 phone photo stores as 698x930 and
// comes up 1.33x short of the Act 4 box). Bounding the short side instead would
// close that gap at roughly 1.8x the stored bytes, and was deliberately not
// taken: these photos are persisted server-side for every patron.
export const PATRON_PHOTO_MAX_DIMENSION = 930

// Explicit JPEG: `canvas.toDataURL()` with no arguments defaults to PNG, which
// for a photograph costs several times the bytes at the same visual quality.
export const PATRON_PHOTO_MIME_TYPE = 'image/jpeg'
export const PATRON_PHOTO_QUALITY = 0.85

// JPEG has no alpha channel, and the canvas spec composites a transparent
// canvas onto solid *black* on serialization. The file pickers accept any
// `image/*`, so a patron choosing a background-removed PNG out of their photo
// library would otherwise get black blotches; paint the canvas first.
export const PATRON_PHOTO_BACKGROUND = '#fff'

// The size a source should be drawn at: its own dimensions bounded by
// maxDimension on the longest side, never upscaled.
export function getPatronPhotoTargetSize(source, maxDimension = PATRON_PHOTO_MAX_DIMENSION) {
  // <img> reports naturalWidth, <video> videoWidth, and a canvas or
  // ImageBitmap only width: a <video>'s `width` is its content attribute and
  // is 0 unless the markup set one.
  const sourceWidth = source?.naturalWidth || source?.videoWidth || source?.width || 0
  const sourceHeight = source?.naturalHeight || source?.videoHeight || source?.height || 0
  if (!sourceWidth || !sourceHeight) {
    throw new Error('Image has no dimensions to resize yet')
  }

  const scale = Math.min(1, maxDimension / Math.max(sourceWidth, sourceHeight))

  // An extreme aspect ratio (a panorama) can round the short side to 0, which
  // neither drawImage nor toDataURL reports as an error — it just yields a
  // blank image. Keep the degenerate case at one pixel instead.
  return {
    width: Math.max(1, Math.round(sourceWidth * scale)),
    height: Math.max(1, Math.round(sourceHeight * scale)),
  }
}

// A correctly sized, opaque canvas ready to draw `source` into. Callers own the
// drawImage so they can apply their own transform first (the kiosk capture
// un-mirrors the live preview).
export function createPatronPhotoCanvas(source, maxDimension = PATRON_PHOTO_MAX_DIMENSION) {
  const { width, height } = getPatronPhotoTargetSize(source, maxDimension)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const context = canvas.getContext('2d')
  if (!context) {
    throw new Error('Canvas 2d context unavailable')
  }
  context.fillStyle = PATRON_PHOTO_BACKGROUND
  context.fillRect(0, 0, width, height)

  return { canvas, context }
}

// Draws any canvas-drawable image source (an <img>, a <video> frame) into a
// correctly-sized canvas and returns it as a data URL. Never upscales: a photo
// smaller than the target is left at its own dimensions.
export function resizeToPatronPhotoDataUrl(source, options = {}) {
  const {
    maxDimension = PATRON_PHOTO_MAX_DIMENSION,
    mimeType = PATRON_PHOTO_MIME_TYPE,
    quality = PATRON_PHOTO_QUALITY,
  } = options

  const { canvas, context } = createPatronPhotoCanvas(source, maxDimension)
  context.drawImage(source, 0, 0, canvas.width, canvas.height)

  return canvas.toDataURL(mimeType, quality)
}

// Reads a user-selected file (phone photo library, file picker) and resolves it
// as a right-sized patron photo data URL.
export function fileToPatronPhotoDataUrl(file, options = {}) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onerror = () => reject(reader.error ?? new Error('Could not read the selected file'))
    reader.onload = () => {
      const image = new window.Image()
      image.onerror = () => reject(new Error('Could not decode the selected image'))
      image.onload = () => {
        try {
          resolve(resizeToPatronPhotoDataUrl(image, options))
        } catch (error) {
          reject(error)
        }
      }
      image.src = reader.result
    }

    reader.readAsDataURL(file)
  })
}
